import { assertNever } from "@redotech/util/type";

// If we add to here, please also add to getProviderEnabledTeams in server/src/utils/coverage-monitor.ts
export enum Provider {
  COMMENTSOLD = "commentsold",
  SHOPIFY = "shopify",
  COMMERCE_CLOUD = "commerce-cloud",
  BIGCOMMERCE = "bigcommerce",
  NETSUITE = "netsuite",
  MANUAL = "manual",
  TIKTOK = "tiktok",
  AMAZON = "amazon",
  WALMART = "walmart",
  EBAY = "ebay",
  APPAREL_MAGIC = "apparel-magic",
  REDO_MARKETPLACE = "redo-marketplace",
  SFOM = "sfom",
  AIMS360 = "aims360",
  CIN7_OMNI = "cin7-omni",
  CIN7_CORE = "cin7-core",
  REDO_API = "redo-api", // See the OrdersApi integration (used by Porsche)
  CUSTOM_RETURN_INTEGRATION = "custom-return-integration",
  REDO = "redo", // Redo native checkout (agentic commerce)
  FAIRE = "faire",
  WOOCOMMERCE = "woocommerce",
  WAYFAIR = "wayfair",
  SPS_COMMERCE = "sps-commerce",
  BRIGHTPEARL = "brightpearl",
  // Backward compatibility: preserve the value stored on legacy Trackstar orders.
  LINNWORKS_TRACKSTAR = "linnworks",
  LINNWORKS_DIRECT = "linnworks-direct",
  TEMU = "temu",
  RITHUM = "rithum",
  APTOS = "aptos",
  FINALE = "finale",
}

/**
 * Whether a provider's refund executor can put money back on the shopper's
 * original payment method. Mirrors the dispatch in the `createRefund` activity:
 * providers that fall through to a no-op or an "Unknown provider" throw cannot,
 * so offering the rail to the shopper would promise a refund we never issue.
 */
export enum OriginalPaymentRefundSupport {
  SUPPORTED = "supported",
  UNSUPPORTED = "unsupported",
  /**
   * The platform itself moves no money; a separate payment integration does.
   * Resolving this needs the team's refund integrations and the order's payment
   * gateways, so callers must finish the check with
   * `isEligibleForOriginalPaymentRefund`.
   */
  REQUIRES_REFUND_INTEGRATION = "requires-refund-integration",
}

export function providerOriginalPaymentRefundSupport(
  provider: Provider | null | undefined,
): OriginalPaymentRefundSupport {
  if (!provider) {
    return OriginalPaymentRefundSupport.UNSUPPORTED;
  }

  switch (provider) {
    case Provider.SHOPIFY:
    case Provider.COMMERCE_CLOUD:
    case Provider.BIGCOMMERCE:
    case Provider.COMMENTSOLD:
    case Provider.NETSUITE:
      return OriginalPaymentRefundSupport.SUPPORTED;
    case Provider.CUSTOM_RETURN_INTEGRATION:
      return OriginalPaymentRefundSupport.REQUIRES_REFUND_INTEGRATION;
    case Provider.SFOM:
    case Provider.APTOS:
    case Provider.MANUAL:
    case Provider.TIKTOK:
    case Provider.AMAZON:
    case Provider.WALMART:
    case Provider.EBAY:
    case Provider.APPAREL_MAGIC:
    case Provider.REDO_MARKETPLACE:
    case Provider.AIMS360:
    case Provider.CIN7_OMNI:
    case Provider.CIN7_CORE:
    case Provider.REDO_API:
    case Provider.REDO:
    case Provider.FAIRE:
    case Provider.WOOCOMMERCE:
    case Provider.WAYFAIR:
    case Provider.SPS_COMMERCE:
    case Provider.BRIGHTPEARL:
    case Provider.LINNWORKS_TRACKSTAR:
    case Provider.LINNWORKS_DIRECT:
    case Provider.TEMU:
    case Provider.RITHUM:
    case Provider.FINALE:
      return OriginalPaymentRefundSupport.UNSUPPORTED;
    default:
      return assertNever(provider);
  }
}

export function providerSupportsAdvancedExchanges(
  provider: Provider | null | undefined,
): boolean {
  if (!provider) {
    return false;
  }

  switch (provider) {
    case Provider.SHOPIFY:
    case Provider.BIGCOMMERCE:
    case Provider.COMMERCE_CLOUD:
      return true;
    case Provider.COMMENTSOLD:
    case Provider.NETSUITE:
    case Provider.MANUAL:
    case Provider.TIKTOK:
    case Provider.AMAZON:
    case Provider.WALMART:
    case Provider.EBAY:
    case Provider.APPAREL_MAGIC:
    case Provider.REDO_MARKETPLACE:
    case Provider.SFOM:
    case Provider.AIMS360:
    case Provider.CIN7_OMNI:
    case Provider.CIN7_CORE:
    case Provider.REDO_API:
    case Provider.CUSTOM_RETURN_INTEGRATION:
    case Provider.REDO:
    case Provider.FAIRE:
    case Provider.WOOCOMMERCE:
    case Provider.WAYFAIR:
    case Provider.SPS_COMMERCE:
    case Provider.BRIGHTPEARL:
    case Provider.LINNWORKS_TRACKSTAR:
    case Provider.LINNWORKS_DIRECT:
    case Provider.TEMU:
    case Provider.RITHUM:
    case Provider.APTOS:
    case Provider.FINALE:
      return false;
    default:
      return assertNever(provider);
  }
}

/** Shared so the sync-order action and its handler cannot disagree about what is supported. */
export const SYNC_ORDER_SUPPORTED_PROVIDERS = [
  Provider.SHOPIFY,
  Provider.COMMENTSOLD,
  Provider.BIGCOMMERCE,
  Provider.COMMERCE_CLOUD,
  Provider.APTOS,
  Provider.NETSUITE,
] as const;

export type SyncOrderSupportedProvider =
  (typeof SYNC_ORDER_SUPPORTED_PROVIDERS)[number];

const syncOrderSupported: ReadonlySet<string> = new Set(
  SYNC_ORDER_SUPPORTED_PROVIDERS,
);

/** Narrows for the handler's switch, so adding a provider here is the only edit needed. */
export function isSyncOrderSupportedProvider(
  provider: string,
): provider is SyncOrderSupportedProvider {
  return syncOrderSupported.has(provider);
}

/** Providers whose orders are built by the custom return integration `constructOrder`. */
export const CUSTOM_INTEGRATION_ORDER_PROVIDERS = [
  Provider.CUSTOM_RETURN_INTEGRATION,
  Provider.NETSUITE,
  Provider.APTOS,
] as const;

const customIntegrationOrderProviders: ReadonlySet<string> = new Set(
  CUSTOM_INTEGRATION_ORDER_PROVIDERS,
);

export function isCustomIntegrationOrderProvider(
  provider: string | undefined | null,
): boolean {
  return !!provider && customIntegrationOrderProviders.has(provider);
}

/**
 * Providers whose same-item exchanges Redo records but never turns into an
 * exchange order: the merchant ships the replacement from their own system.
 */
export const EXTERNALLY_FULFILLED_EXCHANGE_PROVIDERS = [
  Provider.APTOS,
] as const;

const externallyFulfilledExchangeProviders: ReadonlySet<string> = new Set(
  EXTERNALLY_FULFILLED_EXCHANGE_PROVIDERS,
);

export function providerFulfillsExchangesExternally(
  provider: string | undefined | null,
): boolean {
  return !!provider && externallyFulfilledExchangeProviders.has(provider);
}

/** Providers whose order syncs write the returns and claims `Order`. */
export const RETURNS_ORDER_SYNC_PROVIDERS = [
  Provider.APTOS,
  Provider.BIGCOMMERCE,
  Provider.COMMENTSOLD,
  Provider.COMMERCE_CLOUD,
  Provider.CUSTOM_RETURN_INTEGRATION,
  Provider.NETSUITE,
  Provider.SFOM,
  Provider.SHOPIFY,
] as const;

export type ReturnsOrderSyncProvider =
  (typeof RETURNS_ORDER_SYNC_PROVIDERS)[number];

// Marketplaces with no Provider integration yet, as plain lowercase text.
// Shrinks as integrations land: the entry moves into the Provider enum.
const IMPORTED_MARKETPLACES = ["costco"] as const;

/** Channels the shipping-label offline-return flow can attribute. */
export const LABEL_SCAN_MARKETPLACES = [
  ...Object.values(Provider),
  ...IMPORTED_MARKETPLACES,
] as const;
export type LabelScanMarketplace = (typeof LABEL_SCAN_MARKETPLACES)[number];
